import { UITransform, Mat4, Rect, Mask, Node, v3 } from 'cc';

const matrix = new Mat4();
const worldMatrix = new Mat4();
const tempParentMatrix = new Mat4();
const boxSet: Rect[] = [];

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface UITransform {
        /**
         * 只考慮可見子節點的 UITransform.prototype.getBoundingBoxTo
         * @param parentMat 父節點矩陣
         * @deprecated since 2025/07/01, use getVisibleBoundingBoxByRectSet instead
         */
        getVisibleBoundingBox(parentMat: Mat4): Rect;
        /**
         * 只考慮可見子節點的 UITransform.prototype.getBoundingBoxToWorld
         * @deprecated since 2025/07/01, use getVisibleWorldBoundingBoxByRectSet instead
         */
        getVisibleWorldBoundingBox(): Rect;
        /**
         * 不包含子節點的 UITransform.prototype.getBoundingBoxToWorld
         */
        getWorldBoundingBox(): Rect;

        /**
         * 只考慮可見子節點的 UITransform.prototype.getBoundingBoxToWorld
         * 原本傳遞 Parent 矩陣給子節點，更改為傳入 node 來決定 rect 相對位置
         * 集合後再一起做 union，避免途中 Parent 的 BoundingBox 被更動導致 Parent 矩陣錯誤
         * @param node
         * @param excludeNodes 不希望被包含在計算內的節點陣列
         */
        getVisibleBoundingBoxByRectSet(node: Node, excludeNodes?: Node[]): Rect;
        /**
         * @param excludeNodes 不希望被包含在計算內的節點陣列
         */
        getVisibleWorldBoundingBoxByRectSet(excludeNodes?: Node[]): Rect;
    }
}

UITransform.prototype.getVisibleWorldBoundingBox = function getVisibleWorldBoundingBox(this: UITransform): Rect {
    const parentMat = new Mat4();
    this.node.parent.getWorldMatrix(parentMat);
    return this.getVisibleBoundingBox(parentMat);
};

UITransform.prototype.getVisibleBoundingBox = function getVisibleBoundingBox(this: UITransform, parentMat: Mat4): Rect {
    Mat4.fromRTS(matrix, this.node.getRotation(), this.node.getPosition(), this.node.getScale());
    const { width, height } = this.contentSize;
    const rect = new Rect(-this.anchorPoint.x * width, -this.anchorPoint.y * height, width, height);

    Mat4.multiply(worldMatrix, parentMat, matrix);
    rect.transformMat4(worldMatrix);

    // query child's BoundingBox
    if (!this.node.children || this.getComponent(Mask)) {
        return rect;
    }

    tempParentMatrix.set(worldMatrix);
    this.node.children.forEach((childNode) => {
        if (childNode?.active) {
            const uiTransform = childNode.getComponent(UITransform);
            if (uiTransform) {
                parentMat.set(tempParentMatrix);
                const childRect = uiTransform.getVisibleBoundingBox(parentMat);
                if (childRect) {
                    Rect.union(rect, rect, childRect);
                }
            }
        }
    });

    return rect;
};

UITransform.prototype.getWorldBoundingBox = function getWorldBoundingBox(this: UITransform): Rect {
    this.node.parent.getWorldMatrix(worldMatrix);
    return this.getBoundingBox().transformMat4(worldMatrix);
};

UITransform.prototype.getVisibleBoundingBoxByRectSet = function getVisibleWorldBoundingBoxByRectSet(
    this: UITransform,
    node: Node,
    excludeNodes: Node[] = [],
): Rect {
    const getBoundingBoxSet = (self: UITransform) => {
        if (excludeNodes.includes(self.node)) {
            return;
        }

        Mat4.fromRTS(matrix, self.node.getWorldRotation(), self.node.getWorldPosition(), self.node.getWorldScale());
        const { width, height } = self.contentSize;
        const rect = new Rect(-self.anchorPoint.x * width, -self.anchorPoint.y * height, width, height);
        rect.transformMat4(matrix);
        if (!self.node.children || self.getComponent(Mask)) {
            boxSet.push(rect);
            return;
        }
        self.node.children.forEach((childNode) => {
            if (childNode?.active) {
                const uiTransform = childNode.getComponent(UITransform);
                if (uiTransform) {
                    getBoundingBoxSet(uiTransform);
                }
            }
        });
        boxSet.push(rect);
    };

    getBoundingBoxSet(this);

    const rect = boxSet[0];
    boxSet.forEach((box) => {
        Rect.union(rect, rect, box);
    });
    boxSet.splice(0, boxSet.length);
    if (node) {
        const temp = v3(rect.x, rect.y);
        node.transform.convertToNodeSpaceAR(temp, temp);
        rect.x = temp.x;
        rect.y = temp.y;
    }
    return rect;
};

UITransform.prototype.getVisibleWorldBoundingBoxByRectSet = function getVisibleWorldBoundingBoxByRectSet(this: UITransform, excludeNodes: Node[]): Rect {
    return this.getVisibleBoundingBoxByRectSet(null, excludeNodes);
};
